// src/pages/BrokerLogin.jsx
import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  getAuth,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  onAuthStateChanged,
} from "firebase/auth";
import { getFirestore, doc, getDoc } from "firebase/firestore";
import app from "../firebase";
import { Helmet } from "react-helmet-async";
import { useToast } from "../context/ToastContext";

const auth = getAuth(app);
const db = getFirestore(app);

const getFriendlyFirebaseError = (code) => {
  const map = {
    "auth/invalid-email": "Please enter a valid email address.",
    "auth/user-disabled": "This account has been disabled. Contact support.",
    "auth/too-many-requests": "Too many login attempts. Please try again later.",
    "auth/network-request-failed": "Network error. Please check your connection.",
  };
  const sensitive = ["auth/user-not-found", "auth/wrong-password", "auth/invalid-credential"];
  if (sensitive.includes(code)) return "Incorrect email or password.";
  return map[code] || "An unexpected error occurred. Please try again.";
};

export default function BrokerLogin() {
  const navigate = useNavigate();
  const { addToast } = useToast();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(true);          // initial auth check
  const [authenticating, setAuthenticating] = useState(false);

  // Never get stuck on "Checking broker access..."
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      (async () => {
        try {
          if (!user) {
            setLoading(false); // show form for signed-out users
            return;
          }
          // Look up application status in Firestore
          const appRef = doc(db, "broker_applications", user.uid);
          const snap = await getDoc(appRef);
          const status = snap.exists() ? snap.data()?.status : undefined;

          if (status === "approved") {
            navigate("/broker/dashboard"); // gate passed
            return;
          }
          // Not approved (or no application) → show form, don't block
        } catch (err) {
          console.error("Broker preload check failed:", err);
        } finally {
          setLoading(false);
        }
      })();
    });
    return () => unsub();
  }, [navigate]);

  const handleLogin = async () => {
    if (!email || !password) {
      addToast("Please enter your email and password.", "warning");
      return;
    }

    setAuthenticating(true);
    try {
      const cred = await signInWithEmailAndPassword(auth, email, password);
      const appRef = doc(db, "broker_applications", cred.user.uid);
      const snap = await getDoc(appRef);

      if (!snap.exists()) {
        addToast("No broker account found for these credentials.", "error");
        await auth.signOut();
        return;
      }

      const status = snap.data()?.status;

      if (status === "approved") {
        addToast("Welcome back!", "success");
        navigate("/broker/dashboard");
      } else if (status === "pending") {
        addToast("Your broker account is still pending admin approval.", "warning");
        await auth.signOut();
      } else if (status === "rejected") {
        addToast("Your broker application was not approved. Contact support for details.", "error");
        await auth.signOut();
      } else {
        addToast("Unable to determine account status. Please contact support.", "error");
        await auth.signOut();
      }
    } catch (e) {
      addToast(getFriendlyFirebaseError(e.code), "error");
    } finally {
      setAuthenticating(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!email) {
      addToast("Please enter your email to reset password.", "warning");
      return;
    }
    try {
      await sendPasswordResetEmail(auth, email);
      addToast("Reset link sent to your email.", "success");
    } catch (e) {
      addToast(getFriendlyFirebaseError(e.code), "error");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-500">
        Checking broker access...
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <title>Your Choice Associates | Broker Login</title>
        <meta name="description" content="Broker login for Your Choice Associates." />
        <link rel="canonical" href="https://yourchoiceassociates.org/broker-login" />
      </Helmet>

      {/* Top bar */}
      <div className="w-full bg-[#002b5c] text-white py-4 px-6 flex items-center justify-between shadow-md">
        <img src="/images/logo.png" alt="Logo" className="h-10" />
        <span className="text-sm opacity-80">Broker Access</span>
      </div>

      {/* Body */}
      <div className="flex flex-1">
        <div className="w-full max-w-xl mx-auto flex flex-col justify-center px-6 md:px-10 bg-white">
          <div className="mt-12 md:mt-0">
            <h2 className="text-3xl font-extrabold text-[#002b5c] mb-3">Welcome Back</h2>
            <p className="text-sm text-gray-500 mb-8">
              Sign in to access your broker dashboard
            </p>

            <div className="space-y-6">
              <div>
                <label className="block text-sm text-gray-700 mb-1">Email Address</label>
                <input
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleLogin()}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-[#002b5c]"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-700 mb-1">Password</label>
                <input
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleLogin()}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-[#002b5c]"
                />
              </div>

              <div className="flex items-center justify-between text-sm">
                <label className="flex items-center space-x-2 text-gray-600">
                  <input type="checkbox" className="form-checkbox text-[#002b5c]" />
                  <span>Remember me</span>
                </label>
                <button
                  type="button"
                  onClick={handleForgotPassword}
                  className="text-[#002b5c] hover:underline"
                >
                  Forgot Password?
                </button>
              </div>

              <button
                type="button"
                onClick={handleLogin}
                disabled={authenticating}
                className="w-full bg-[#002b5c] text-white py-3 rounded-lg hover:bg-[#013a76] transition font-semibold shadow-md disabled:opacity-60"
              >
                {authenticating ? "Logging in..." : "Log In"}
              </button>

              <p className="text-sm text-center text-gray-600 pt-2">
                Not a broker yet?{" "}
                <Link to="/broker-signup" className="text-[#e31c3d] font-medium hover:underline">
                  Apply here
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
